import {Injectable} from '@angular/core';
import {
  AuditLogPage,
  AuditLogsService,
  Configuration as SalesTerminalConf,
  DefaultService,
  LoginResponse,
  LoginService,
  LogoutRequest,
  Permission,
  RedirectResponse,
  RefreshRequest,
  ResetPasswordDetailResponse,
  User,
  UserBasicPut,
  UsersService
} from '../../generated/sales-terminal-client';
import {Configuration as TranslationConf} from '../../generated/translation-client';
import {Configuration as ReportConf, OwnerType} from '../../generated/report-client';
import {Configuration as ReportAccountingConf} from '../../generated/report-accounting-client';
import {Configuration as CustomerConf} from '../../generated/customer-client';
import {Configuration as S3DownloadConf, DownloadService} from '../../generated/s3-download-client';
import {StorageService} from '../storage/storage.service';
import {SnackService} from '../snack/snack.service';
import {LanguageService} from '../language/language.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {UtilService} from '../util/util.service';
import {ViewService} from '../view/view.service';
import {RouteService} from '../route/route.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import moment from 'moment';
import {find, uniqBy} from 'lodash-es';
import {TranslateService} from '@ngx-translate/core';
import {catchError, Observable, of, retry, Subject, tap} from 'rxjs';
import {AuthUser} from "./auth-user.model";
import {map, switchMap} from "rxjs/operators";
import {TranslationsListComponent} from '../../modules/admin/translations/list/translations-list.component';
import {TemplatesListComponent} from '../../modules/admin/templates/list/templates-list.component';
import {ConfirmDialogComponent} from '../../components/confirm-dialog/confirm-dialog.component';
import {Privileges} from '../../modules/admin/privileges/privileges.constants';
import {environment} from '../../../environments/environment';
import {ProxiedLoginService, ProxiedPasswordService, ProxiedRegistrationService, ProxiedUsersService} from '../proxy/proxy.service';
import {CentrePropertyService} from "../centre-property/centre-property.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  static STORAGE_AUTH_ACCESS_TOKEN = 'auth.access-token';
  static STORAGE_AUTH_SKIP_CUSTOMER_SCREEN_CLOSURE = 'auth.skip-customer-screen-closure';
  static STORAGE_AUTH_REMIND_CREDENTIALS = 'auth.remind-credentials';
  static STORAGE_AUTH_ACTIVE_SALES_POINT_ID = 'auth.active-sales-point-id';
  static STORAGE_AUTH_ACTIVE_ROLE_ID = 'auth.active-role-id';
  static STORAGE_AUTH_USER_LOGGED_IN = 'auth.user_logged_in';
  static STORAGE_AUTH_USER_LOGGED_OUT = 'auth.user_logged_out';
  static STORAGE_AUTH_RELOAD_PAGE = 'auth.reload_page';
  static STORAGE_AUTH_REFRESH_TOKEN = 'auth.refresh-token';
  static STORAGE_AUTH_USER_ACTIVATION_STARTED = 'auth.user_activation_started';
  static STORAGE_AUTH_USER_LOGIN_TIMEOUT = 'auth.user_activation_login_timeout';
  static STORAGE_AUTH_LEGAL_INVITATION_ACCEPTED = 'auth.legal_invitation_accepted';

  private _isLoggedIn = false;
  private _jwt: string;
  private _permissions: Permission[];
  private _refreshToken: string; //token to refresh access token
  public salespointId: number;
  public isAliasSp: boolean;
  public roleId: number;
  public salesPointCurrencySymbol: string;
  public salesPointCurrencyCent: string;
  public user: AuthUser;
  private _apiHelp: string;
  private _contactHtml: string;
  private _ticketControlHtml: string;
  public caller: boolean;
  public hasAlias: boolean;
  private _salesPointLanguage: string;
  private _refreshTokenStarted: boolean;
  private _refreshTokenCron: any;
  private _welcomePageHtml: string;

  public pendingChangesActive: boolean;
  public pendingChangeAction: Function;
  public pendingChangeListener: Subject<boolean> = new Subject<boolean>();

  constructor(
    private passwordService: ProxiedPasswordService,
    private loginService: LoginService,
    private proxiedLoginService: ProxiedLoginService,
    private proxiedRegistrationService: ProxiedRegistrationService,
    private proxiedUsersService: ProxiedUsersService,
    private proxiedPasswordService: ProxiedPasswordService,
    private usersService: UsersService,
    private translationConf: TranslationConf,
    private salesTerminalConf: SalesTerminalConf,
    private reportConf: ReportConf,
    private reportAccountingConf: ReportAccountingConf,
    private customerConf: CustomerConf,
    private s3DownloadConf: S3DownloadConf,
    public jwtHelper: JwtHelperService,
    private snackService: SnackService,
    private utilService: UtilService,
    private translateService: TranslateService,
    private storageService: StorageService,
    private languageService: LanguageService,
    private router: Router,
    private dialogRef: MatDialog,
    private auditLogsService: AuditLogsService,
    private viewService: ViewService,
    private routeService: RouteService,
    private oauthService: DefaultService,
    private centrePropertyService: CentrePropertyService
  ) {
    utilService.tokenExpired.subscribe(() => {
      if (this.user?.userId !== 'sandbox') {
        this.logout(true);
      }
    });
    utilService.tokenRefresh.subscribe(() => {
      if (!this._refreshTokenStarted) {
        this.changeRole();
      }
    });
    utilService.userLoggedIn.subscribe(() => {
      this.caller = true;
      this.storageService.set(AuthService.STORAGE_AUTH_USER_LOGGED_IN, moment().unix());
    });
    utilService.userLoggedOut.subscribe(() => {
      this.caller = true;
      this.storageService.set(AuthService.STORAGE_AUTH_USER_LOGGED_OUT, moment().unix());
    });
  }

  // store the URL so we can redirect after logging in
  redirectUrl: string;

  get token(): string {
    return this._jwt;
  }

  get permissions(): Permission[] {
    return this._permissions;
  }

  set permissions(value) {
    this._permissions = value;
  }

  get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  set isLoggedIn(value: boolean) {
    this._isLoggedIn = value;
  }

  get currencySymbol(): string {
    return this.salesPointCurrencySymbol || '';
  }

  get salesPointLanguage(): string {
    return this._salesPointLanguage || '';
  }

  get currencyCent(): string {
    return this.salesPointCurrencyCent || '';
  }

  get salespoint(): Permission {
    return this.salespointId && find(this.permissions, { salespointId: this.salespointId }) || {};
  }

  get helpHtml(): string {
    return this._apiHelp;
  }
  set helpHtml(value) {
    this._apiHelp = value;
  }

  get contactHtml(): string {
    return this._contactHtml;
  }
  set contactHtml(value) {
    this._contactHtml = value;
  }

  get ticketControlHtml(): string {
    return this._ticketControlHtml;
  }
  set ticketControlHtml(value) {
    this._ticketControlHtml = value;
  }

  get welcomePageHtml(): string {
    return this._welcomePageHtml;
  }
  set welcomePageHtml(value: string) {
    this._welcomePageHtml = value;
  }

  private cronUpdateToken() {
    if (this.routeService.isWindow) return; // skip token refresh on customer screen to avoid log out
    const expDate = this.jwtHelper.getTokenExpirationDate(this._jwt);
    const diff = moment(expDate).diff(moment(), 'milliseconds');
    if (this._refreshTokenCron) {
      clearTimeout(this._refreshTokenCron);
    }
    this._refreshTokenCron = setTimeout(() => {
      const refreshToken = this.storageService.get(AuthService.STORAGE_AUTH_REFRESH_TOKEN);
      if (refreshToken) {
        const request = {
          roleId: this.roleId,
          salespointId: this.salespointId,
        }
        this.refreshAccess(request, true).subscribe();
      }
    }, diff > 5000 ? diff - 5000 : 0);
  }

  public checkSalespointProperty(property: string): boolean {
    return this.salespoint.salespointProperties?.includes(property);
  }

  public checkPrivileges(privileges: any[] | string, matchAll: boolean = false): boolean {
    let canAccess = matchAll;
    if (!this.user?.privileges) {
      return false;
    }
    if (typeof privileges === 'string') {
      privileges = [privileges];
    }
    privileges.forEach(privilege => {
      if (matchAll && !this.user.privileges.includes(privilege)) {
        canAccess = false;
      } else if (!matchAll && this.user.privileges.includes(privilege)) {
        canAccess = true;
      }
    });
    return canAccess;
  }

  get canAccessSales() {
    return this.checkPrivileges([Privileges.SALES]);
  }

  get canAccessAdmin() {
    return this.canAccessAccounts || this.canAccessClassifiers || this.hasTranslationsPrivilege || this.hasApiUsersPrivilege || this.hasRegistrationDataPrivilege;
  }

  get canAccessBackOffice(): boolean {
    return this.hasAdminDashboardPrivilege || this.hasSelfServiceEventsAccessPrivilege;
  }

  get canAccessReports() {
    return this.hasDailyReportPrivilege || this.hasReportingPromoterDashboardPrivilege || this.hasReportingPromoterMyEventsPrivilege
      || this.hasRegistrationReportPrivilege || this.hasTicketRegistrationPrivilege || this.hasReportingPromoterDetailedReportsPrivilege
      || this.hasReportingVenueOwnerPrivilege || this.hasReportingSalespointSalesPrivilege || this.hasOverEventsReportPrivilege || this.hasReportingSalesSummaryReportPrivilege || this.hasLegalInvoicingViewPrivilege;
  }

  get salespoints(): Permission[] {
    return uniqBy(this.permissions, 'salespointId');
  }

  get primarySalespoints(): Permission[] {
    return this.salespoints.filter(sp => this.findAliasSalespoints.indexOf(sp.salespointId) === -1);
  }

  get aliasSalespoints(): Permission[] {
    return this.salespoints.filter(sp => this.findAliasSalespoints.indexOf(sp.salespointId) !== -1);
  }

  get findAliasSalespoints(): number[] {
    return this.salespoints.filter(sp => sp.alias && sp.alias.id).map(sp => sp.alias.id);
  }

  public checkStorage(): Observable<boolean> {
    const jwt = this.storageService.get(AuthService.STORAGE_AUTH_ACCESS_TOKEN);
    const refreshToken = this.storageService.get(AuthService.STORAGE_AUTH_REFRESH_TOKEN);
    const salespointId = parseInt(this.storageService.get(AuthService.STORAGE_AUTH_ACTIVE_SALES_POINT_ID), 0);
    const roleId = parseInt(this.storageService.get(AuthService.STORAGE_AUTH_ACTIVE_ROLE_ID), 0);
    if (refreshToken) {
      let notExpired = false;
      try {
        notExpired = jwt && !this.checkIfIsExpired(jwt);
      } catch (err) {
        this.storageService.remove(AuthService.STORAGE_AUTH_ACCESS_TOKEN); // faulty jwt
        return of(false);
      }
      if (notExpired) {
        if (this._jwt === jwt) { // if the token has not been changed in storage
          return of(true);
        } else { // the token has been changed in another tab
          return this.loginSuccessObs({ refreshToken: refreshToken, accessToken: jwt });
        }
      } else { // access as expired trying to refresh access
        const request: RefreshRequest = {
          roleId: roleId,
          salespointId: salespointId,
        };
        return this.refreshAccess(request);
      }
    } else {
      return of(false);
    }
  }

  private refreshAccess(request: RefreshRequest, refreshOnly?: boolean): Observable<boolean> {
    this._refreshTokenStarted = true;
    return this.proxiedLoginService.publicRefreshPost(request).pipe(
      tap({error: error => {
          this.getLoginResponseObserver(true, true).error(error);
        }}),
      switchMap(value => this.getLoginResponseObserver(refreshOnly).next(value))
    );
  }

  private getLoginResponseObserver(refreshOnly?: boolean, emitTokenExpired?: boolean) { // refreshOnly / tokenExpired
    return {
      next: (response: LoginResponse) => this.loginSuccessObs(response, refreshOnly),
      error: (err: any, displayError?: boolean) => {
        if (emitTokenExpired) {
          this._refreshTokenStarted = false;
          this.utilService.tokenExpired.emit();
        }
        if (displayError) {
          this.snackService.showBackendError(err);
        }
      }
    };
  }

  private checkIfIsExpired(jwt: string) {
    return this.jwtHelper.isTokenExpired(jwt);
  }

  public loginObs(email: string, password: string, rememberMe: boolean): Observable<boolean> {
    this._refreshTokenStarted = true;
    return this.proxiedLoginService.publicLoginPost({ email, password }).pipe(
      tap({
          next: () => {(rememberMe) ? this.remindCredentials(email) : this.forgetCredentials()},
          error: error => this.getLoginResponseObserver().error(error, true)
        }
      ),
      switchMap(value => this.getLoginResponseObserver().next(value))
    );
  }

  public changeRole(salespointId?: number, roleId?: number): Observable<boolean> {
    const request = {
      roleId: roleId ? roleId : this.roleId,
      salespointId: salespointId ? salespointId : this.salespointId,
    }
    return this.refreshAccess(request);
  }

  public updateUser(oldPassword: string, newPassword: string) {
    return this.passwordService.userPasswordPut({
      oldPassword,
      newPassword
    }).pipe(
      switchMap(value => this.getLoginResponseObserver().next(value))
    );
  }

  public forgotPassword(email: string): Observable<any> {
    return this.passwordService.publicUserPasswordForgotPost({ email: email }).pipe(
      tap({error: (e) => console.error(e)})
    )
  }

  public checkResetLink(secret: string): Observable<ResetPasswordDetailResponse> {
    return this.passwordService.publicUserPasswordResetGet(secret).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public resetPassword(password: string, secret: string): Observable<any> {
    return this.passwordService.publicUserPasswordResetPost({ password: password }, secret).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getCredentials() {
    return this.storageService.get(AuthService.STORAGE_AUTH_REMIND_CREDENTIALS);
  }

  public remindCredentials(username: string) {
    this.storageService.set(AuthService.STORAGE_AUTH_REMIND_CREDENTIALS, username);
  }

  public forgetCredentials() {
    this.storageService.remove(AuthService.STORAGE_AUTH_REMIND_CREDENTIALS);
  }

  loginSuccessObs(loginResponse: LoginResponse, refreshOnly?: boolean, noRedirect?: boolean): Observable<boolean> {
    const jwt = loginResponse.accessToken;
    if (jwt) {
      this._jwt = jwt;
      this.storageService.set(AuthService.STORAGE_AUTH_SKIP_CUSTOMER_SCREEN_CLOSURE, refreshOnly);
      this.storageService.set(AuthService.STORAGE_AUTH_ACCESS_TOKEN, this._jwt);
      if (noRedirect) {
        this.utilService.userLoggedInNoRedirect = true;
      }
      this.utilService.userLoggedIn.emit();
    }
    if (loginResponse.refreshToken) {
      this._refreshToken = new Date().toDateString();
      this.storageService.set(AuthService.STORAGE_AUTH_REFRESH_TOKEN, this._refreshToken);
    }

    if (loginResponse.legals?.length) {
      this.storageService.set(AuthService.STORAGE_AUTH_LEGAL_INVITATION_ACCEPTED, JSON.stringify(loginResponse.legals));
    }

    // References global components/securitySchemes of OpenAPI document.
    this.updateOpenApiCredentials({ bearerAuth: this._jwt });
    this.updateS3Paths();

    this.cronUpdateToken();
    this.isLoggedIn = true;

    if (refreshOnly) {
      return of(true);
    } else {
      try {
        const decodeToken = this.jwtHelper.decodeToken(this._jwt);
        this.user = {
          userId: decodeToken.user_id,
          profile: null,
          email: decodeToken.email,
          firstName: decodeToken.first_name || '?',
          lastName: decodeToken.last_name || '?',
          centreId: decodeToken.centre_id,
          isSuperCentre: decodeToken.super_centre_id && decodeToken.super_centre_id === decodeToken.salespoint_id,
          isSubCentre: decodeToken.centre_id && decodeToken.centre_id === decodeToken.salespoint_id && decodeToken.super_centre_id !== decodeToken.salespoint_id,
          isSalespointUnderSuperCentre: decodeToken.centre_id === decodeToken.super_centre_id && decodeToken.centre_id !== decodeToken.salespoint_id,
          isNewTypeSalespoint: parseInt(decodeToken.salespoint_id) > 10000000,
          superCentreId: decodeToken.super_centre_id,
          privileges: decodeToken.privileges,
          language: decodeToken.default_language,
          timezone: decodeToken.timezone,
          supportsYbo: decodeToken.supports_ybo,
          picture: ''
        };
        this.user.hasDashboard = !this.user.isSuperCentre && !this.user.isSalespointUnderSuperCentre;
        this._refreshTokenStarted = false
        return this.getPermissions();
      } catch (e) {
        this.snackService.showBackendError(e);
        return of(false);
      }
    }
  }

  getPermissions() {
    const decodeToken = this.jwtHelper.decodeToken(this._jwt);
    return this.loginService.getPermissions()
      .pipe(retry(3))
      .pipe(switchMap((value: Permission[]) => {return this.handlePermissions(value, decodeToken);}))
      .pipe(tap(value => {if (!value) this.logout();}, error => {this.snackService.showBackendError(error);}))
  }

  public refreshPermissions(): void {
    this.getPermissions().subscribe();
  }

  public oauthLogin(state: string): Observable<RedirectResponse> {
    return this.oauthService.loginOauthGet(state).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  handlePermissions(permissions: Permission[], decodeToken: any): Observable<boolean> {
    if (permissions.length == 0) {
      console.error('User does not have any permissions.');
      return of(false);
    }
    this.permissions = permissions;
    if (this.languageService.selectedLang !== this.user.language && !this.storageService.get(LanguageService.STORAGE_LANGUAGE_UPDATED)) {
      this.languageService.changeLang(this.user.language, true);
    }

    const salesPoint = decodeToken.salespoint_id && decodeToken.role_id && find(permissions, { salespointId: decodeToken.salespoint_id, role: { id: decodeToken.role_id } }) || permissions[0];
    if (!this.salespointId || this.salespointId !== salesPoint.salespointId) {
      this.salespointId = salesPoint.salespointId;
      this.storageService.set(AuthService.STORAGE_AUTH_ACTIVE_SALES_POINT_ID, this.salespointId);
    }
    if (!this.roleId || this.roleId !== decodeToken.role_id) {
      this.roleId = decodeToken.role_id;
      this.storageService.set(AuthService.STORAGE_AUTH_ACTIVE_ROLE_ID, this.roleId);
    }
    this.user.salespointId = salesPoint.salespointId;
    this.user.isPromoter = !!salesPoint.isPromoter;
    this.user.centreName = salesPoint.centreName;
    this.user.salespointName = salesPoint.salespointName;
    this.user.roleName = salesPoint.role?.name;
    this.myProfile().subscribe({
      next: (profile) => {
        this.user.profile = profile;
        if (salesPoint.legalPerson) {
          const profileLegal = find(this.user.profile.legalPersons, { legalPersonId: salesPoint.legalPerson.id });
          this.user.legalPersonCountryCode = profileLegal?.countryCode || null;
          this.user.isPrivateAccount = salesPoint.legalPerson.isPlaceholder;
          this.user.companyName = salesPoint.legalPerson.companyName;
          this.user.legalPersonId = salesPoint.legalPerson.id;
          this.user.isLegalBankAccountVerified = salesPoint.legalPerson.bankAccountVerified;
          if (!this.isEventEdit) {
            this.showBankVerificationNotification();
          }
          if (salesPoint.legalPerson.projectManager) {
            this.user.projectManager = salesPoint.legalPerson.projectManager;
          }
        }
        if (profile.pendingEmail) {
          this.dialogRef.open(ConfirmDialogComponent, {
            panelClass: 'xs-popup',
            data: {
              title: this.translateService.instant('general.api_users_pending_email_title'),
              description: this.translateService.instant('general.api_users_pending_email_description', { email: '<b>' + profile.pendingEmail + '</b>' }),
              buttonOk: this.translateService.instant('general.api_users_pending_email_button_ok'),
              buttonCancelHidden: true
            }
          });
        }
      },
      error: err => this.snackService.showBackendError(err)
    });
    this.salesPointCurrencySymbol = salesPoint.currencySymbol;
    this.salesPointCurrencyCent = salesPoint.currencyCent;
    this._salesPointLanguage = salesPoint.salespointDefaultLang;
    this.isAliasSp = !!find(permissions, { alias: { id: this.salespointId } });
    this.hasAlias = !!salesPoint.alias;
    this.utilService.userPermissionsFetched.next(true);
    this.languageService.getCentreTranslations().subscribe(result => {
      this.helpHtml = result.centre?.api_help_menu || '';
      this.contactHtml = result.centre?.api_menu_contact_us_description || '';
      this.ticketControlHtml = result.centre?.api_qr_ticket_control_description || 'centre.api_qr_ticket_control_description';
      this.welcomePageHtml = result.centre?.api_registration_user_confirmation_success_description || '';
    });
    this.centrePropertyService.fetchCentreProperties(this.salespointId);
    return of(true);
  }

  public userLogout(): void {
    this.pendingChangeListener.next(true);
    if (this.pendingChangesActive) {
      this.pendingChangeAction = this.logout;
      this.router.navigate(['/login']);
    } else {
      this.logout();
    }
  }

  public logout(tokenExpired: boolean = false, expireOnlyCurrent: boolean = false): void {
    if (this._refreshTokenCron) {
      clearTimeout(this._refreshTokenCron);
    }
    let logout;
    if (!tokenExpired) {
      let request: LogoutRequest = undefined; // Expire all by default
      if (expireOnlyCurrent) request = { refreshToken: this.storageService.get(AuthService.STORAGE_AUTH_REFRESH_TOKEN) }; // Expire only current if true
      logout = this.proxiedLoginService.logoutPost(request).pipe(
        map(() => { return of(true)}),
        catchError(() => of(false)));
    } else {
      logout = of(true);
    }
    logout.subscribe(() => {
      // Close all opened dialogs
      this.dialogRef.closeAll();

      // Unset user logged in state
      this.isLoggedIn = false;

      // Null everything on logout
      this.redirectUrl = '';
      this.utilService.userLoggedInNoRedirect = false;
      this.centrePropertyService.fetchCentreProperties();
      this.updateOpenApiCredentials(null);
      this._jwt = '';
      this._refreshToken = '';
      this.user = new AuthUser();
      this.permissions = [];
      this.salespointId = null;
      this.roleId = null;
      this.isAliasSp = false;
      this.helpHtml = '';
      this.contactHtml = '';

      // Get stored user credentials before local storage is cleared
      const credentials = this.getCredentials();
      // Clear local storage
      this.storageService.clear();
      // Prefill inputs with stored credentials if available
      if (credentials) {
        this.remindCredentials(credentials);
      }

      // Set default language
      this.languageService.changeLang(this.languageService.selectedLang);
      // Redirect to Login page
      this.router.navigate(['/login']);
      // Trigger event that user was logged out completely
      this.utilService.userLoggedOut.emit();
    });
  }

  private updateOpenApiCredentials(settings: { bearerAuth?: string }): void {
    this.translationConf.credentials = settings;
    this.salesTerminalConf.credentials = settings;
    this.reportConf.credentials = settings;
    this.reportAccountingConf.credentials = settings;
    this.customerConf.credentials = settings;
    this.proxiedLoginService.configuration.credentials = settings;
    this.proxiedRegistrationService.configuration.credentials = settings;
    this.proxiedUsersService.configuration.credentials = settings;
    this.proxiedPasswordService.configuration.credentials = settings;
  }

  private updateS3Paths(): void {
    if (!environment.proxy) {
      const downloadService = new DownloadService(null, undefined, {} as S3DownloadConf);
      this.s3DownloadConf.basePath = downloadService.configuration.basePath + '/' + this.viewService.conf.s3Env;
    }
  }

  public myProfile(): Observable<User> {
    return this.usersService.userGet().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public saveMyProfile(data: UserBasicPut): Observable<User> {
    return this.usersService.userPut(data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public reloadPage() {
    // check if the tab needs reloading
    if (!this._jwt || this._jwt === '' || this._jwt !== this.storageService.get(AuthService.STORAGE_AUTH_ACCESS_TOKEN)) {
      location.reload();
    }
  }

  public clearSalespointCache(): void {
    this.storageService.remove(TranslationsListComponent.STORAGE_SEARCH_RESULTS_SELECTED_LANGUAGES);
    this.storageService.remove(TemplatesListComponent.STORAGE_SEARCH_RESULTS_SELECTED_LANGUAGES);
  }

  public getAuditLogs(entityId: string, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getUserAuditLogsByUserUuid(entityId, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getRoleAuditLogsByLogId(entityId: number, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getRoleAuditLogsByRoleId(entityId, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public navigateToBaseUrl(): Promise<boolean> {
    return this.routeService.navigate(this.baseUrl);
  }

  get baseUrl(): string {
    let baseUrl = '/';
    if (this.hasAdminDashboardPrivilege) baseUrl = '/app/back-office/dashboard';
    else if (this.hasReportingPromoterDashboardPrivilege || this.hasReportingPromoterMyEventsPrivilege) baseUrl = this.reportsRoute;
    else if (this.canAccessSales) baseUrl = '/app/sales-terminal';
    else if (this.hasSelfServiceEventsAccessPrivilege) baseUrl = '/app/back-office/events';
    return baseUrl;
  }

  get accessDeniedRoute(): string {
    return '/app/access-denied';
  }
  get adminRoute(): string {
    // Accounts category
    if (this.hasUserGetPrivilege) return '/app/admin/users';
    else if (this.hasLegalGetPrivilege) return '/app/admin/legal-persons';
    else if (this.hasCustomersAccessPrivileges) return '/app/admin/customers';
    else if (this.hasRolesAccessPrivilege) return '/app/admin/roles';
    else if (this.hasPrivilegesManagePrivilege) return '/app/admin/privileges';
    // Classifiers category
    else if (this.hasSalespointManagePrivilege) return '/app/admin/salespoints';
    else if (this.hasCategoriesAccessPrivilege) return '/app/admin/categories';
    // Translations category
    else if (this.hasTranslationsPrivilege) return '/app/admin/translations';
    // Other admin menu items
    else if (this.hasApiUsersPrivilege) return '/app/admin/api-users';
    else if (this.hasRegistrationDataPrivilege) return '/app/admin/registration-data';

    else return this.accessDeniedRoute;
  }
  get reportsRoute(): string {
    if (this.hasReportingPromoterDashboardPrivilege) return '/app/reports/overview';
    else if (this.hasDailyReportPrivilege) return '/app/reports/daily-reports';
    else if (this.hasReportingPromoterMyEventsPrivilege) return '/app/reports/events';
    else if (this.hasReportingVenueOwnerPrivilege) return '/app/reports/venue-events';
    else if (this.hasReportingPromoterDetailedReportsPrivilege) return '/app/reports/detailed-reports';
    else if (this.hasReportingSalespointSalesPrivilege) return '/app/reports/salespoint-sales';
    else if (this.hasRegistrationReportPrivilege) return '/app/reports/ticket-registration-reports';
    else if (this.hasTicketRegistrationPrivilege) return '/app/reports/ticket-registration';
    else if (this.hasOverEventsReportPrivilege) return '/app/reports/over-events-report';
    else if (this.hasReportingSalesSummaryReportPrivilege) return '/app/reports/sales-summary-report';
    else if (this.hasLegalInvoicingViewPrivilege) return '/app/reports/invoices-report';
    else return this.accessDeniedRoute;
  }
  get backOfficeRoute(): string {
    if (this.hasSelfServiceEventsAccessPrivilege) return '/app/back-office/events';
    else return this.accessDeniedRoute;
  }

  public showBankVerificationNotification(fixed?: boolean): void {
    if (this.isBankAccountUnverified && this.hasBankAccountViewPrivilege) {
      this.snackService.showInfo(this.getBankVerificationNotification, fixed, () => {
        this.routeService.navigate('/app/admin/legal-persons/view/' + this.user.legalPersonId, { queryParams: { tab: 'billing', skipNotification: true } });
      }, this.translateService.instant('general.api_legal_persons_bank_account_button_verify'));
    }
  }

  get getBankVerificationNotification(): string {
    return this.translateService.instant('general.api_legal_persons_bank_account_unverified');
  }

  get isBankAccountUnverified(): boolean {
    return this.viewService.conf.environment && !this.user.isPrivateAccount && !this.user.isLegalBankAccountVerified;
  }

  public setSandboxUser(): void {
    this.user = {
      userId: 'sandbox',
      profile: null,
      email: null,
      firstName: null,
      lastName: null,
      centreId: 7,
      isSuperCentre: false,
      isSubCentre: false,
      isSalespointUnderSuperCentre: true,
      isNewTypeSalespoint: true,
      superCentreId: 7,
      privileges: [Privileges.SELF_SERVICE_EVENTS_VIEW, Privileges.SELF_SERVICE_EVENTS_MANAGE],
      language: 'et',
      timezone: 'Europe/Tallinn',
      supportsYbo: false,
      picture: ''
    };
    this._salesPointLanguage = 'et';
    this.salesPointCurrencySymbol = '€';
  }

  get canAccessAccounts(): boolean {
    return this.hasUserGetPrivilege || this.hasLegalGetPrivilege || this.hasCustomersAccessPrivileges || this.hasRolesAccessPrivilege || this.hasPrivilegesManagePrivilege;
  }
  get canAccessClassifiers(): boolean {
    return this.hasSalespointManagePrivilege || this.hasCategoriesAccessPrivilege;
  }

  get hasUserGetPrivilege(): boolean {
    return this.checkPrivileges([Privileges.USER_GET]);
  }
  get hasUserManagePrivilege(): boolean {
    return this.checkPrivileges([Privileges.USER_CHANGE]);
  }
  get hasLegalGetPrivilege(): boolean {
    return this.checkPrivileges([Privileges.LEGAL_PERSON_GET]);
  }
  get hasLegalChangePrivilege(): boolean {
    return this.checkPrivileges([Privileges.LEGAL_PERSON_CHANGE]);
  }
  get hasLegalInvoicingViewPrivilege(): boolean {
    return this.checkPrivileges([Privileges.LEGAL_PERSON_INVOICING_VIEW]);
  }
  get hasBalanceSummaryViewPrivilege(): boolean {
    return this.checkPrivileges([Privileges.LEGAL_PERSON_INVOICING_VIEW]);
  }
  get hasBankAccountViewPrivilege(): boolean {
    return this.checkPrivileges([Privileges.LEGAL_PERSON_BANK_ACCOUNT_VIEW]);
  }
  get hasCustomersAccessPrivileges(): boolean {
    return this.checkPrivileges([Privileges.CUSTOMER_MANAGE]);
  }
  get hasRolesAccessPrivilege(): boolean {
    return this.checkPrivileges([Privileges.ROLES_MANAGE]);
  }
  get hasPrivilegesManagePrivilege(): boolean {
    return this.checkPrivileges([Privileges.PRIVILEGES_MANAGE]);
  }

  get hasSalespointManagePrivilege(): boolean {
    return this.checkPrivileges([Privileges.SALESPOINT_MANAGE]);
  }
  get hasFeesAccessPrivilege(): boolean {
    return this.checkPrivileges([Privileges.FEE_MANAGE]);
  }
  get hasCategoriesAccessPrivilege(): boolean {
    return this.checkPrivileges([Privileges.CATEGORY_MANAGE]);
  }
  get hasTranslationsPrivilege(): boolean {
    return this.checkPrivileges([Privileges.TRANSLATIONS_ADMIN]);
  }
  get hasTranslationsDownloadPrivilege(): boolean {
    return this.checkPrivileges([Privileges.TRANSLATIONS_ADMIN_DOWNLOAD]);
  }
  get hasPublicVenuesAdminPrivilege(): boolean {
    return this.user.isSuperCentre && this.checkPrivileges([Privileges.PUBLIC_VENUES_ADMIN]); // Only super-centre users with privilege can admin.
  }
  get hasApiUsersPrivilege(): boolean {
    return this.checkPrivileges([Privileges.API_USERS_MANAGE]);
  }
  get hasRegistrationDataPrivilege(): boolean {
    return this.checkPrivileges([Privileges.DELETE_REGISTRATION_DATA]);
  }

  get hasReportingPromoterDashboardPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_PROMOTER_DASHBOARD]);
  }
  get hasReportingPromoterMyEventsPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_PROMOTER_MY_EVENTS]);
  }
  get hasDailyReportPrivilege(): boolean {
    return this.checkPrivileges([Privileges.DAILY_REPORT]);
  }
  get hasRegistrationReportPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REGISTRATION_REPORT]);
  }
  get hasTicketRegistrationPrivilege(): boolean {
    return this.checkPrivileges([Privileges.TICKET_REGISTRATION]);
  }
  get hasReportingPromoterDetailedReportsPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_PROMOTER_DETAILED]);
  }
  get hasReportingSalespointSalesPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_SALESPOINT_SALES]);
  }
  get hasOverEventsReportPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_OVER_EVENTS]);
  }
  get hasReportingSalesSummaryReportPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_EVENT_SALES_SUMMARY]);
  }

  get hasSelfServiceEventsAccessPrivilege(): boolean {
    return this.checkPrivileges([Privileges.SELF_SERVICE_EVENTS_VIEW, Privileges.SELF_SERVICE_EVENTS_MANAGE]);
  }
  get hasSelfServiceEventsManagePrivilege(): boolean {
    return this.checkPrivileges([Privileges.SELF_SERVICE_EVENTS_MANAGE]);
  }
  get hasSelfServiceEventsViewPrivilege(): boolean {
    return this.checkPrivileges([Privileges.SELF_SERVICE_EVENTS_VIEW]);
  }
  get hasSelfServiceEventsFullAccessPrivilege(): boolean {
    return this.checkPrivileges([Privileges.EVENTS_FULL_ACCESS]);
  }
  get hasAdminDashboardPrivilege(): boolean {
    return this.checkPrivileges([Privileges.ADMIN_DASHBOARD]);
  }

  get hasRefundAccessPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REFUND_ALL, Privileges.REFUND_SALES_TERMINAL, Privileges.REFUND_WEB, Privileges.REFUND_PROMOTER]) && this.user.supportsYbo;
  }
  get hasSelfServicePaymentReversalPrivilege(): boolean {
    return this.checkPrivileges([Privileges.SELF_SERVICE_TRANSACTIONS_PAYMENT_REVERSAL]);
  }
  get hasRefundExchangePrivilege(): boolean {
    return this.checkPrivileges([Privileges.REFUND_EXCHANGE]);
  }
  get hasBookingViewPrivilege(): boolean {
    return this.checkPrivileges([Privileges.BOOKING_VIEW]);
  }

  get hasProjectManagersAccessPrivileges(): boolean {
    return this.checkPrivileges([Privileges.USER_GET, Privileges.LEGAL_PERSON_PROJECT_MANAGER], true);
  }
  get hasAuditGetPrivilege(): boolean {
    return this.checkPrivileges([Privileges.AUDIT_GET]);
  }

  get hasReportingVenueOwnerPrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_VENUE_OWNER]);
  }
  get isVenueReport(): boolean {
    return this.routeService.checkRoute(['reports', 'venue-events']);
  }
  get isEventEdit(): boolean {
    return this.routeService.checkRoute(['back-office', 'events', 'edit']);
  }
  get ownerType(): OwnerType {
    return this.isVenueReport ? OwnerType.VenueOwner : OwnerType.EventOwner;
  }
  get hasReportingVenueOwnerShowRevenuePrivilege(): boolean {
    return this.checkPrivileges([Privileges.REPORTING_VENUE_OWNER_SHOW_REVENUE]);
  }
  get canSeeReportsMonetaryValues(): boolean {
    return !this.hasReportingVenueOwnerPrivilege || this.hasReportingVenueOwnerShowRevenuePrivilege;
  }

  get hasReportingSuperCentreAccess(): boolean {
    return this.user.isSuperCentre && this.hasLegalGetPrivilege;
  }

  get hasTicketToolPrivilege(): boolean {
    return this.checkPrivileges(Privileges.TICKET_TOOL_VIEW);
  }
}
